/*
 * Copyright (c) 2019-2021 LogMeIn
 * All Rights Reserved Worldwide.
 *
 * THIS PROGRAM IS CONFIDENTIAL AND PROPRIETARY TO LOGMEIN
 * AND CONSTITUTES A VALUABLE TRADE SECRET.
 */

import 'proxy-polyfill';
import 'core-js/stable';
import 'regenerator-runtime/runtime';
import 'url-polyfill';
import React, { ReactElement } from 'react';
import ReactDOM from 'react-dom';
import createCache from '@emotion/cache';
import createCssVarsRevert from 'css-vars-revert';
import { CacheProvider } from '@emotion/react';

import {
  getLocale,
  getTranslationsForLocale,
} from './services/TranslationService';
import { sendJoinStartEvent } from './lib/telemetry';
import {
  ErrorCode,
  ErrorPagesInterface,
  FlowType,
  getHandoverParams,
  HandoverParams,
  readLaunchTokensFromIndexPage,
  ThemeType,
} from './services/IndexPageReaderService';
import { init as initSentry } from './lib/sentry';
import { createFallbackLaunchTokens } from './context/SessionContext';
import { redirectIfWebFirst } from './services/RedirectService';
import { removeQueryParametersAndFragmentsFromUrl } from './services/UrlService';
import { GoToMeetingPageLayout } from './components/Layouts/GoToMeetingPageLayout';
import { JoinFlow } from './flows/JoinFlow';
import { InstallFlow } from './flows/InstallFlow';
import { BreakoutUnsupportedBrowser } from './pages/GoTo/Error/BreakoutUnsupportedBrowser';
import { GoToError } from './pages/GoTo/Error/GoToError';
import { EndToEndEncryptionError } from './pages/GoTo/Error/EndToEndEncryptionError';
import { isIE } from './services/ClientService';
import { GoToPageLayout } from './components/Layouts/GoToPageLayout';
import { PageProps } from './components/Layouts/Layout';
import { GoToUnsupported } from './pages/GoTo/Error/GoToUnsupported';
import { TelemetryService } from './services/TelemetryService';
import { MobileFlow } from './components/MobilePages/MobileFlow/MobileFlow';
import { Unsupported } from './pages/G2M/Error/Unsupported';
import { Error } from './pages/G2M/Error/Error';
import {
  isInstantJoinTechnicallySupported,
  isMobileJoinTechnicallySupported,
  isNativeEndpointTechnicallySupported,
} from './services/ProductCompatibilityService';

export const ie11SaveCache = createCache({
  key: 'css',
  stylisPlugins: [createCssVarsRevert()],
});

initSentry();

const locale = getLocale();

document.getElementsByTagName('html')[0].setAttribute('lang', locale);

const handoverParams = getHandoverParams();
const launchTokens =
  readLaunchTokensFromIndexPage() ?? createFallbackLaunchTokens(handoverParams);
const sessionParams = {
  ...handoverParams,
  ...launchTokens,
  telemetryService: new TelemetryService(
    handoverParams.ulfEnvConfig.telemetryServiceBaseUrl
  ),
};

const translationsForLocale = getTranslationsForLocale(
  locale,
  handoverParams.product
);

sendJoinStartEvent(
  sessionParams.telemetryService,
  handoverParams.meetingId,
  handoverParams.sessionTrackingId
);
removeQueryParametersAndFragmentsFromUrl();
const redirectToWeb = redirectIfWebFirst(sessionParams);

const pageParams: PageProps = {
  translationsForLocale: translationsForLocale,
  locale: locale,
  sessionParams: sessionParams,
};

const errorPages: ErrorPagesInterface = {
  [ErrorCode.BreakoutUnsupportedBrowser]: (
    <GoToMeetingPageLayout {...pageParams}>
      <BreakoutUnsupportedBrowser />
    </GoToMeetingPageLayout>
  ),
  [ErrorCode.MissingUserAgent]: (
    <GoToPageLayout {...pageParams}>
      <GoToUnsupported />
    </GoToPageLayout>
  ),
  [ErrorCode.UnsupportedBrowser]: (
    <GoToPageLayout {...pageParams}>
      <GoToUnsupported />
    </GoToPageLayout>
  ),
  [ErrorCode.E2eeUnsupportedOperatingSystem]: (
    <GoToPageLayout {...pageParams}>
      <EndToEndEncryptionError />
    </GoToPageLayout>
  ),
  [ErrorCode.MalformedJsonWebToken]: (
    <GoToPageLayout {...pageParams}>
      <GoToError />
    </GoToPageLayout>
  ),
  [ErrorCode.MissingProperty]: (
    <GoToPageLayout {...pageParams}>
      <GoToError />
    </GoToPageLayout>
  ),
  [ErrorCode.E2eeUnsupportedEapStatus]: (
    <GoToPageLayout {...pageParams}>
      <GoToError />
    </GoToPageLayout>
  ),
};

const getInstallOrJoinFlow = (params: HandoverParams): JSX.Element => {
  return params.flowType === FlowType.Install ? <InstallFlow /> : <JoinFlow />;
};

const getThemedUnsupportedPage = (params: HandoverParams): JSX.Element => {
  return params.theme.themeType === ThemeType.GoToTheme ? (
    errorPages[ErrorCode.UnsupportedBrowser]
  ) : (
    <GoToMeetingPageLayout {...pageParams}>
      <Unsupported />
    </GoToMeetingPageLayout>
  );
};

const getThemedGenericErrorPage = (params: HandoverParams): JSX.Element => {
  return params.theme.themeType === ThemeType.GoToTheme ? (
    <GoToPageLayout {...pageParams}>
      <GoToError />
    </GoToPageLayout>
  ) : (
    <GoToMeetingPageLayout {...pageParams}>
      <Error />
    </GoToMeetingPageLayout>
  );
};

type ThemeToLayout = {
  [key in ThemeType]: React.FC<PageProps>;
};

const PageLayout: ThemeToLayout = {
  [ThemeType.GoToTheme]: GoToPageLayout,
  [ThemeType.GoToMeetingTheme]: GoToMeetingPageLayout,
};
const Page = PageLayout[handoverParams.theme.themeType];

const isError: boolean = !!handoverParams.error;
const isMobile: boolean = isMobileJoinTechnicallySupported;
const isNativeEndpointAllowed: boolean =
  isNativeEndpointTechnicallySupported &&
  !handoverParams.meetingInfo.sessionOptions.nativeEndpointDisabled;
const isUnsupportedBrowser: boolean = !isInstantJoinTechnicallySupported;

export const page = (): JSX.Element => {
  let content: ReactElement;
  if (isError) {
    content = errorPages[handoverParams.error!.code];
  } else if (isMobile) {
    content = (
      <Page {...pageParams}>
        <MobileFlow />
      </Page>
    );
  } else if (isNativeEndpointAllowed) {
    content = (
      <Page {...pageParams}>{getInstallOrJoinFlow(handoverParams)}</Page>
    );
  } else if (isUnsupportedBrowser) {
    content = getThemedUnsupportedPage(handoverParams);
  } else {
    content = getThemedGenericErrorPage(handoverParams);
  }

  return isIE ? (
    <CacheProvider value={ie11SaveCache}>{content}</CacheProvider>
  ) : (
    content
  );
};

//Do not render any page if redirect to web is supported and enabled
ReactDOM.render(redirectToWeb ? <></> : page(), document.getElementById('app'));
